import { Plane, Clock, Ticket, Calendar, File } from "lucide-react";
import { FlightActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { ActivityCost, ActivityLink } from "./ActivityFields";

interface FlightCardProps {
  activity: FlightActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onSaveToLibrary?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
  unsaved?: boolean;
}

export const FlightCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onSaveToLibrary,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = false,
  isAdmin = false,
  unsaved = false,
}: FlightCardProps) => {

  const firstTravel = activity.travels?.[0];


  const renderAlwaysVisible = () => (
    <>
      {firstTravel &&
        (<>
          <div className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            <span>{firstTravel.flightNumber}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{firstTravel.fromTime} - {firstTravel.toTime}</span>
          </div>
          <ActivityCost cost={activity.cost} isAdmin={isAdmin} isTraveler={isTraveler} />
        </>)}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Flights */}
      {activity.travels && activity.travels.length > 0 && (
        <div className="mb-4">
          <span className="font-medium mb-1 block">Flight Details</span>
          <div className="space-y-2">
            {activity.travels.map((travel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex space-x-2 flex-row">
                  <span className="font-medium">{travel.flightNumber}</span>
                  <span> - </span>
                  <span className="text-gray-600">{travel.airline}</span>
                </div>
                <div className="pt-2 text-gray-600 flex flex-row items-center gap-4">
                  <div className="flex flex-col">
                    <div>{travel.from}</div>
                    <div className="flex flex-row items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <div className="pr-2">{travel.fromDate}</div>
                      <Clock className="h-3 w-3" />
                      <div>{travel.fromTime}</div>
                    </div>
                  </div>
                  <div> → </div>
                  <div className="flex flex-col">
                    <div>{travel.to}</div>
                    <div className="flex flex-row items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <div className="pr-2">{travel.toDate}</div>
                      <Clock className="h-3 w-3" />
                      <div>{travel.toTime}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      {activity.documentation && activity.documentation.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activity.documentation.map((doc, index) => (
            <a
              key={index}
              href={doc.url}
              download
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 mt-2 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <File className="h-3 w-3 flex-shrink-0" />
              <span>{doc.name}</span>
            </a>
          ))}
        </div>
      )}

      {/* Link */}
      <ActivityLink link={activity.link} isAdmin={isAdmin} isTraveler={isTraveler} />
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onSaveToLibrary={onSaveToLibrary}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
      unsaved={unsaved}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
